// v5.2 — Reveal Setup labels. Firm-name examples REMOVED per confidential-use
// philosophy. The `description` lines drive the Step 3 OptionCard.
export const COMPANY_TYPES = [
  { value: "software",  label: "Software / SaaS",          description: "Products, platforms, licences, modules, adoption." },
  { value: "services",  label: "Services / implementation", description: "Delivery, implementation, engineering, managed operations." },
  { value: "cloud",     label: "Cloud / hyperscaler",       description: "Cloud platforms, workloads, migration, architecture." },
  { value: "advisory",  label: "Advisory / consulting",     description: "Strategy, governance, operating model, executive decisions." },
];

// Wave 3i — visible tiles for end-user intake / settings. Keeps 4 options.
// Legacy `technology_operations` is intentionally absent here but still honoured
// as a backend value (rendered as "Services & delivery" in the locked state,
// saved back as `services` on next write).
export const VISIBLE_COMPANY_TYPES = [
  { value: "software", label: "Software", helper: "Products, platforms, licences, modules, adoption." },
  { value: "services", label: "Services", helper: "Delivery, implementation, engineering, managed operations." },
  { value: "cloud", label: "Cloud", helper: "Cloud platforms, workloads, migration, architecture." },
  { value: "advisory", label: "Advisory", helper: "Strategy, governance, operating model, executive decisions." },
];

// Wave 3i — map any saved value (incl. legacy `technology_operations`) to its
// current-UI display label. Used by the locked-state renderer in settings.
export function resolveVisibleCompanyLabel(savedValue) {
  if (savedValue === "technology_operations") return "Services";
  const hit = VISIBLE_COMPANY_TYPES.find((o) => o.value === savedValue);
  return hit ? hit.label : "—";
}

// Wave 3i — when saving, rewrite legacy `technology_operations` to `services`
// so the user drifts into a supported lens over time without a migration script.
export function normalizeCompanyTypeForSave(value) {
  return value === "technology_operations" ? "services" : value;
}

// v5.2 — 4 commercial motions, with descriptions per the spec. Backend enum
// values unchanged (`acquisition`, `retention`, `solutioning`, `delivery`).
// `leadership` is intentionally no longer offered.
const COMMON_ROLE_FAMILIES = [
  { value: "acquisition", label: "Win new business",                description: "Find a credible entry point." },
  { value: "retention",   label: "Grow existing business",          description: "Expand or defend current footprint." },
  { value: "solutioning", label: "Build solutioning & commercials", description: "Shape proof, scope, and commercial case." },
  { value: "delivery",    label: "Deliver existing business",       description: "Protect trust, delivery, and renewal risk." },
];

export const ROLE_FAMILIES_BY_TYPE = {
  software: COMMON_ROLE_FAMILIES,
  services: COMMON_ROLE_FAMILIES,
  cloud: COMMON_ROLE_FAMILIES,
  advisory: COMMON_ROLE_FAMILIES,
};

// Wave 3m — map any saved role_family value (incl. legacy `leadership`) to
// its current-UI display label. Used by any locked-state renderer that shows
// a user their saved motion.
export function resolveVisibleRoleFamilyLabel(savedValue) {
  if (savedValue === "leadership") return "Grow existing business";
  const hit = COMMON_ROLE_FAMILIES.find((o) => o.value === savedValue);
  return hit ? hit.label : "—";
}

// Wave 3m — when saving, rewrite legacy `leadership` to `retention` so the
// user drifts into a supported motion over time without a migration script.
export function normalizeRoleFamilyForSave(value) {
  return value === "leadership" ? "retention" : value;
}

// v5.2 — UI label "Manager" maps to backend value `leader`. UI label
// "Leader" maps to backend value `pl_owner`. Backend enum stays
// {individual, leader, pl_owner}.
export const SENIORITY_OPTIONS = [
  {
    value: "individual",
    label: "Individual Contributor",
    desc: "I own my patch, deals, and stakeholder moves.",
  },
  {
    value: "leader",
    label: "Manager",
    desc: "I lead a team, account, pursuit, or renewal motion.",
  },
  {
    value: "pl_owner",
    label: "Leader",
    desc: "I own a P&L, portfolio, or major revenue line.",
  },
];

// v5.2 — labels per the Reveal Setup spec. Backend keys are STABLE.
export const CAPABILITY_OPTIONS = [
  { value: "ai_data",                  label: "AI, data and analytics" },
  { value: "cloud_infra",              label: "Cloud and infrastructure" },
  { value: "enterprise_saas",          label: "Enterprise apps and workflow" },
  { value: "risk_compliance",          label: "Risk, compliance and controls" },
  { value: "managed_services",         label: "Managed operations" },
  { value: "integration_architecture", label: "Integration and architecture" },
];

export const CAPABILITY_ORDER_BY_TYPE = {
  software: ["enterprise_saas", "ai_data", "integration_architecture", "cloud_infra", "risk_compliance", "managed_services"],
  services: ["ai_data", "managed_services", "integration_architecture", "enterprise_saas", "cloud_infra", "risk_compliance"],
  cloud: ["cloud_infra", "ai_data", "integration_architecture", "managed_services", "enterprise_saas", "risk_compliance"],
  advisory: ["ai_data", "risk_compliance", "integration_architecture", "enterprise_saas", "cloud_infra", "managed_services"],
};

export function getOrderedCapabilities(companyType) {
  const order = CAPABILITY_ORDER_BY_TYPE[companyType];
  if (!order) return CAPABILITY_OPTIONS;
  return order.map((v) => CAPABILITY_OPTIONS.find((o) => o.value === v)).filter(Boolean);
}


// Chunk 3 / TASK 2 — experience-level intake (replaces capability tiles).
// Four single-select options. Drives explanation-depth calibration in
// IC/Manager/Leader synthesis prompts via EXPERIENCE_CALIBRATION_DOCTRINE.
// Backend enum: junior / mid / senior / principal. Persists to
// intake_responses.user_experience_level.
export const EXPERIENCE_LEVELS = [
  {
    value: "junior",
    label: "Junior",
    years: "0–3 years",
    desc: "Early in career, learning the patterns.",
  },
  {
    value: "mid",
    label: "Mid",
    years: "3–7 years",
    desc: "Solid practitioner, building authority.",
  },
  {
    value: "senior",
    label: "Senior",
    years: "7–15 years",
    desc: "Established expert, known in the account.",
  },
  {
    value: "principal",
    label: "Principal",
    years: "15+ years",
    desc: "Practice authority, sets the agenda.",
  },
];

export function getRoleFamilies(companyType) {
  return ROLE_FAMILIES_BY_TYPE[companyType] || [];
}
